import React from 'react';
import { AlertCircle, Braces, Brackets, Code2 } from 'lucide-react';
import { looksLikeJsonString } from '../../utils/jsonDetection';

/**
 * Decide which leading glyph to show for a valid-JSON value.
 * - Parsed object/array (native pg/jsonb) → inspect with Array.isArray.
 * - JSON-looking string → peek first non-whitespace char.
 * - Anything else (primitives, defensive) → keep generic Code2.
 */
function pickJsonGlyph(value: unknown): typeof Braces {
  if (typeof value === 'object' && value !== null) {
    return Array.isArray(value) ? Brackets : Braces;
  }
  if (typeof value === 'string') {
    const head = value.trimStart()[0];
    if (head === '[') return Brackets;
    if (head === '{') return Braces;
  }
  return Code2;
}

export type JsonCellRendererProps = {
  value: unknown;
  columnId: string;
  forcedJsonColumnIds: Set<string>;
  declaredJsonColumnIds: Set<string>;
  jsonColumnIdSet: Set<string>;
  onExpand: () => void;
  cellContent: React.ReactNode;
};

/**
 * Shared JSON cell decoration. Returns null when the column isn't a JSON
 * cell so the caller falls through to its default rendering. When the
 * column IS a JSON cell, returns a Code2 button (or AlertCircle for the
 * declared-on-text bad-JSON fallback) wrapped around `cellContent`.
 *
 * Native PG `jsonb` cells arrive as parsed JS objects; declared-on-text
 * cells arrive as strings. Both shapes are valid JSON content here and
 * both share the canonical `cell-expand-btn` testid.
 */
export function JsonCellRenderer({
  value,
  columnId,
  forcedJsonColumnIds,
  declaredJsonColumnIds,
  jsonColumnIdSet,
  onExpand,
  cellContent,
}: JsonCellRendererProps): React.ReactNode | null {
  const isForced = forcedJsonColumnIds.has(columnId);
  const isDeclared = declaredJsonColumnIds.has(columnId);
  const isHeuristic = jsonColumnIdSet.has(columnId);

  if (!isForced && !isHeuristic) return null;
  if (value === null || value === undefined) return null;

  const isParsedObject = typeof value === 'object';
  const isJsonString = typeof value === 'string' && looksLikeJsonString(value);

  if (isParsedObject || isJsonString) {
    const Glyph = pickJsonGlyph(value);
    return (
      <span className="flex items-center gap-1 min-w-0 -mx-3 px-3 bg-blue-500/[0.04] dark:bg-blue-400/[0.05]">
        <button
          type="button"
          className="inline-flex h-4 w-4 items-center justify-center rounded text-blue-600/70 dark:text-blue-400/70 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-muted/60 flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
          title="View JSON"
          aria-label="View JSON"
          onClick={(event) => {
            event.stopPropagation();
            onExpand();
          }}
          onDoubleClick={(event) => { event.stopPropagation(); }}
          data-testid="cell-expand-btn"
        >
          <Glyph className="h-3 w-3" aria-hidden="true" />
        </button>
        {cellContent}
      </span>
    );
  }

  if (isDeclared && typeof value === 'string' && value.trim().length > 0) {
    return (
      <span className="flex items-center gap-1 min-w-0">
        {/* T6.1c — THIS AMBER STAYS HARDCODED. It was routed here as a token
            violation and the ruling is: `--energy-text` is the WRONG
            destination, so tokenising it would have been the bug.
            `--energy` means "work in flight / not yet committed" and the
            locked accent system bans orange on anything IDLE-PERSISTENT.
            This glyph is idle-persistent by definition — it marks a declared
            JSON column whose stored value does not parse, and it sits there
            for as long as the row is on screen, unchanged by anything the
            user is doing. It is the warning member of the same traffic-light
            axis ruled a sanctioned non-token axis at `ConnectionCardCompact`'s
            `StatusIndicator`; the app has no warning token, and `--destructive`
            overstates it (unparseable data is not a destructive action). Same
            axis, same ruling, so the two stay consistent. */}
        <button
          type="button"
          className="inline-flex h-4 w-4 items-center justify-center rounded text-amber-700 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 hover:bg-muted/60 flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
          title="Invalid JSON — click to view raw value or undeclare"
          aria-label="Invalid JSON — view raw value or undeclare"
          onClick={(event) => {
            event.stopPropagation();
            onExpand();
          }}
          onDoubleClick={(event) => { event.stopPropagation(); }}
          data-testid="cell-json-invalid-btn"
        >
          <AlertCircle className="h-3 w-3" aria-hidden="true" />
        </button>
        {cellContent}
      </span>
    );
  }

  return null;
}
